import React, { useState, useEffect } from 'react';
import { usePortalStore } from '../../store';
import { CheckCircle, XCircle, AlertCircle, ShieldCheck, ShieldAlert, ShieldOff } from 'lucide-react';
import type { GovernorDecisionItem } from '../../types';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

type VerdictBadgeProps = { decision: GovernorDecisionItem };

const VerdictBadge: React.FC<VerdictBadgeProps> = ({ decision }) => {
  const configs = {
    EXECUTE: { bg: 'bg-emerald-900/40', border: 'border-emerald-600', text: 'text-emerald-300', Icon: ShieldCheck },
    QUEUE:   { bg: 'bg-yellow-900/40',  border: 'border-yellow-600',  text: 'text-yellow-300',  Icon: ShieldAlert },
    BLOCK:   { bg: 'bg-red-900/40',     border: 'border-red-600',     text: 'text-red-300',     Icon: ShieldOff   },
  } as const;
  const verdict = decision.routing_verdict in configs ? decision.routing_verdict : 'EXECUTE';
  const { bg, border, text, Icon } = configs[verdict];
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${bg} ${border}`}>
      <Icon className={`w-4 h-4 ${text}`} />
      <span className={`text-sm font-semibold ${text}`}>{verdict}</span>
      <span className="text-xs text-slate-400">risk {decision.risk_score.toFixed(2)}</span>
    </div>
  );
};

export const GateReviewScreen: React.FC = () => {
  const {
    gates, selectGate, selectedGate,
    approveGateApi, rejectGateApi,
    reviseGate, addToast, loadGates,
  } = usePortalStore();
  const [revisionNotes, setRevisionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [governorDecision, setGovernorDecision] = useState<GovernorDecisionItem | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const goBack = () => {
    // Force a re-check: reload gates and clear selection by refreshing
    loadGates();
    // Cast needed as store typing requires Gate but null resets selection
    selectGate(null as unknown as import('../../types').Gate);
  };

  useEffect(() => {
    if (!selectedGate) { setGovernorDecision(null); return; }
    fetch(`${API_BASE}/api/zpc/gates/${selectedGate.id}/governor-decision`)
      .then(r => r.ok ? r.json() : null)
      .then((d: GovernorDecisionItem | null) => setGovernorDecision(d))
      .catch(() => setGovernorDecision(null));
  }, [selectedGate?.id]);

  const pendingGates = gates.filter(g => g.status === 'pending');

  if (!selectedGate || !pendingGates.includes(selectedGate)) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Gate Review</h1>
          <p className="text-slate-400 mt-2">{pendingGates.length} pending decision{pendingGates.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="space-y-3">
          {pendingGates.length === 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-400">All gates approved! Keep shipping.</p>
            </div>
          ) : (
            pendingGates.map(gate => (
              <button
                key={gate.id}
                onClick={() => selectGate(gate)}
                className="w-full text-left bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-100">{gate.artifact.name}</h3>
                    <p className="text-sm text-slate-400 mt-1">{gate.phase}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-200">
                      Score: {gate.qualityScore.average.toFixed(2)}
                    </div>
                    <div className={`text-xs mt-1 ${
                      gate.qualityScore.average >= 4.0 ? 'text-emerald-400' : 'text-yellow-400'
                    }`}>
                      {gate.qualityScore.average >= 4.0 ? '✓ Passes' : '⚠ Below threshold'}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // Detailed gate review
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={goBack}
          className="px-4 py-2 hover:bg-slate-700 rounded-lg text-sm transition-colors"
        >
          ← Back
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-100">{selectedGate.artifact.name}</h1>
            {governorDecision && <VerdictBadge decision={governorDecision} />}
          </div>
          <p className="text-slate-400 mt-1">{selectedGate.phase}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Artifact Content */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Artifact</h2>
          <div className="bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto text-sm text-slate-300 font-mono">
            {selectedGate.artifact.content.split('\n').map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        </div>

        {/* Quality Assessment */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Quality Assessment</h2>

          <div className="space-y-3 mb-6">
            {[
              { label: 'Correctness', score: selectedGate.qualityScore.correctness },
              { label: 'Security', score: selectedGate.qualityScore.security },
              { label: 'Performance', score: selectedGate.qualityScore.performance },
              { label: 'Maintainability', score: selectedGate.qualityScore.maintainability },
              { label: 'Accessibility', score: selectedGate.qualityScore.accessibility },
              { label: 'Reliability', score: selectedGate.qualityScore.reliability },
              { label: 'Architectural Fit', score: selectedGate.qualityScore.architecturalFitness },
            ].map(metric => (
              <div key={metric.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-400">{metric.label}</span>
                  <span className="text-sm font-semibold text-slate-200">{metric.score.toFixed(1)}</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      metric.score >= 4 ? 'bg-emerald-500' : metric.score >= 3.5 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(metric.score / 5) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-slate-900 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              {selectedGate.qualityScore.average >= 4 ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-yellow-500" />
              )}
              <span className="font-semibold text-slate-100">Average Score</span>
            </div>
            <div className="text-2xl font-bold text-slate-100">
              {selectedGate.qualityScore.average.toFixed(2)} / 5.0
            </div>
            <div className="text-xs text-slate-400 mt-2">
              Threshold: 4.0 {selectedGate.qualityScore.average >= 4 ? '✓' : '✗'}
            </div>
          </div>

          {selectedGate.qualityScore.performance < 4 && (
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3 text-sm text-yellow-200 mb-6">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Performance metrics below target. Consider adding NFR numbers.
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h3 className="font-semibold text-slate-100 mb-4">Decision</h3>

        <div className="space-y-3">
          <button
            onClick={async () => {
              setActionBusy(true);
              await approveGateApi(selectedGate.id);
              setActionBusy(false);
              goBack();
            }}
            disabled={actionBusy}
            className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-white font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            {actionBusy ? 'Saving...' : 'Approve & Advance'}
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <textarea
              value={revisionNotes}
              onChange={e => setRevisionNotes(e.target.value)}
              placeholder="Notes for revision..."
              className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <button
              onClick={async () => {
                setActionBusy(true);
                await reviseGate(selectedGate.id, revisionNotes);
                addToast('Revision notes saved', 'info');
                setActionBusy(false);
              }}
              disabled={actionBusy}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 rounded-lg text-white font-semibold transition-colors"
            >
              Revise
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Rejection reason..."
              className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <button
              onClick={async () => {
                setActionBusy(true);
                await rejectGateApi(selectedGate.id, rejectReason || 'Does not meet criteria');
                setActionBusy(false);
                goBack();
              }}
              disabled={actionBusy}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white font-semibold transition-colors flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {actionBusy ? '...' : 'Reject'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
